import { useEffect, useMemo, useState } from "react";
import { useI18n } from "../i18n";
import { loadDirectory } from "../data";
import { EmploymentBadge } from "../components/Badges";
import { AddEmployee } from "./AddEmployee";
import { ImportEmployees } from "./ImportEmployees";
import type { DirectoryRow, EmploymentStatus, Workspace } from "../types";

type View = "list" | "add" | "import";

export function Employees({ ws, onOpen }: { ws: Workspace; onOpen: (employmentId: string) => void }) {
  const { t, pick } = useI18n();
  const companyId = ws.membership.company_id;
  const canManage = ws.permissions.has("employees.manage");
  const [rows, setRows] = useState<DirectoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"" | EmploymentStatus>("");
  const [dept, setDept] = useState("");
  const [view, setView] = useState<View>("list");

  async function refresh() {
    try {
      setRows(await loadDirectory(companyId));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    }
  }
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const departments = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rows ?? []) if (r.department_id) m.set(r.department_id, pick(r.department_name ?? "", r.department_name_ar));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows, pick]);

  const shown = (rows ?? []).filter((r) => {
    if (status && r.status !== status) return false;
    if (dept && r.department_id !== dept) return false;
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return [r.full_name, r.full_name_ar, r.employee_number, r.job_title, r.job_title_ar]
      .some((v) => (v ?? "").toLowerCase().includes(q));
  });

  if (view === "add") {
    return <AddEmployee ws={ws} onDone={(id) => { setView("list"); void refresh(); if (id) onOpen(id); }} />;
  }
  if (view === "import") {
    return <ImportEmployees ws={ws} onDone={() => { setView("list"); void refresh(); }} />;
  }

  return (
    <div className="page">
      <div className="toolbar">
        <input className="search" id="employee-search" type="search" placeholder={t("searchEmployees")} value={query}
          onChange={(e) => setQuery(e.target.value)} aria-label={t("searchEmployees")} />
        <select id="status-filter" value={status} onChange={(e) => setStatus(e.target.value as "" | EmploymentStatus)} aria-label={t("status")}>
          <option value="">{t("allStatuses")}</option>
          <option value="active">{t("statusActive")}</option>
          <option value="probation">{t("statusProbation")}</option>
          <option value="starting">{t("statusStarting")}</option>
          <option value="terminated">{t("statusTerminated")}</option>
        </select>
        {departments.length > 0 && (
          <select id="dept-filter" value={dept} onChange={(e) => setDept(e.target.value)} aria-label={t("department")}>
            <option value="">{t("department")}</option>
            {departments.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        )}
        {canManage && (
          <div className="row toolbar-end">
            <button type="button" className="btn secondary" onClick={() => setView("import")}>{t("importEmployees")}</button>
            <button type="button" className="btn primary" onClick={() => setView("add")}>{t("addEmployee")}</button>
          </div>
        )}
      </div>
      {error && <p className="alert danger" role="alert">{error}</p>}
      {rows === null ? (
        <p className="muted">{t("loading")}</p>
      ) : rows.length === 0 ? (
        <div className="empty">
          <h2>{t("employees")}</h2>
          <p className="muted">{t("noEmployees")}</p>
        </div>
      ) : (
        <section className="card flush">
          <div className="table-wrap">
            <table className="clickable">
              <thead>
                <tr>
                  <th>{t("fullName")}</th>
                  <th>{t("employeeNumber")}</th>
                  <th>{t("jobTitle")}</th>
                  <th className="hide-sm">{t("department")}</th>
                  <th className="hide-sm">{t("hireDate")}</th>
                  <th>{t("status")}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.employment_id} onClick={() => onOpen(r.employment_id)} tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter") onOpen(r.employment_id); }}>
                    <td className="strong">{pick(r.full_name, r.full_name_ar)}</td>
                    <td className="mono">{r.employee_number}</td>
                    <td>{pick(r.job_title ?? "—", r.job_title_ar)}</td>
                    <td className="hide-sm">{r.department_name ? pick(r.department_name, r.department_name_ar) : "—"}</td>
                    <td className="hide-sm mono">{r.hire_date}</td>
                    <td><EmploymentBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted table-foot">{shown.length} / {rows.length}</p>
        </section>
      )}
    </div>
  );
}
